import React, { memo } from 'react';

import { FEATURE_ROTATE_90_DEGREES_BUTTON } from '../../flags';
import { EditorStarterItem } from '../../items/item-type';
import { CanHaveRotation } from '../../items/shared';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { InspectorSubLabel } from '../components/inspector-label';
import { NumberControlUpdateHandler } from './number-controls';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { Rotate90DegreesCw, RotateRight } from '@crabs-ai/web-icons';
import { ToolButton } from '@crabs-ai/ds-react';

const RotationControlUnmemoized: React.FC<{
  rotation: number;
  itemId: string;
}> = ({ rotation, itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<EditorStarterItem, number>(
    itemId,
    (item) => ('rotation' in item ? item.rotation : 0),
  );

  const onRotation: NumberControlUpdateHandler = React.useCallback(
    ({ num, commitToUndoStack }) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            // Mixed selection can contain audio or whole groups. They have no
            // rotation contract; do not add an ignored property or rewrite them.
            if (i.type === 'group' || !('rotation' in i)) return i;
            const updatedItem: CanHaveRotation = {
              ...(i as CanHaveRotation),
              rotation: num,
            };
            return updatedItem as EditorStarterItem;
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const onRotateRight = React.useCallback(() => {
    setState({
      update: (state) => {
        return changeItemsWithSelection(state, itemId, targetIds, (i) => {
          if (i.type === 'group' || !('rotation' in i)) {
            return i;
          }

          const updatedItem: CanHaveRotation = {
            ...(i as CanHaveRotation),
            rotation: (i.rotation + 90) % 360,
          };
          return updatedItem as EditorStarterItem;
        });
      },
      commitToUndoStack: true,
    });
  }, [setState, itemId, targetIds]);

  return (
    <div>
      <InspectorSubLabel>
        {t('Editor.inspector.text.rotation')}
      </InspectorSubLabel>
      <div className="flex items-center gap-4xs">
        <span className="w-[96px]">
          <ValueInputField
            isShowLeadingNode={true}
            leadingNodeType="icon"
            leadingNodeValue={
              <RotateRight className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
            }
            isUnit="º"
            setValue={onRotation}
            value={rotation}
            min={null}
            max={null}
            step={1}
            accessibilityLabel="Rotation"
            hasMixedValue={isMixed}
          />
        </span>
        {FEATURE_ROTATE_90_DEGREES_BUTTON && (
          <ToolButton
            onClick={onRotateRight}
            aria-label="Rotate Right"
          >
            <Rotate90DegreesCw />
          </ToolButton>
        )}
      </div>
    </div>
  );
};

export const RotationControl = memo(RotationControlUnmemoized);
